<template>
  <div v-if="type == 'WEEK'" class="flex flex-col flex-1 min-h-0">
    <div class="scroll-container flex-1 min-h-0">
      <div class="parent text-sm overflow-x-auto">
        <div class="sticky-header">
          <div class="weekDay"></div>
          <div class="weekDay"></div>
          <div class="weekDay"></div>
          <div v-for="item of weekTableHead" :key="item.label" class="weekDay card">
            <span class="hidden md:inline">{{ item.label }}</span>
            <span class="inline md:hidden">{{ abbreviateDay(item.label) }}</span>
          </div>
          <div class="weekDay"></div>
          <div class="weekDay"></div>
          <div class="weekDay"></div>
          <div v-for="date of weekTableHead.map((w) => w.value)" :key="date" class="weekDay card">
            {{ date }}
          </div>
        </div>

        <template v-for="company of weekData?.data" :key="company.companyName">
          <template v-for="unity of company.healthUnities" :key="unity.healthUnitName">
            <template v-if="shouldShowUnity(unity)">
              <div
                class="period text-[11px] md:text-[14px] flex gap-1 md:gap-2 items-center justify-center h-8 md:h-12 bg-gray-100"
              >
                <span>{{ company.companyName }}</span
                ><span>-</span>
                <LabelBadge
                  v-if="unity.healthUnitIsActive === false"
                  label="Inativa"
                  color="red"
                  size="small"
                  :icon="null"
                />
                <span class="font-bold">{{ unity.healthUnitName }}</span>
              </div>

              <template v-for="scale of unity.schedules" :key="scale.scheduleId">
                <template v-if="canReadSchedule(scale.scheduleId)">
                  <div
                    class="flex flex-col justify-center items-center font-bold bg-white sticky-column"
                    :style="{
                      gridRow: `span ${scale.shifts
                        .flatMap((s) => s.hours)
                        .reduce((a, b) => a + b.positions.length, 0)}`
                    }"
                    :title="scale.scheduleName"
                  >
                    {{ scale.scheduleName }}
                    <span class="text-xs font-normal hidden md:inline">{{
                      getTimezoneDetails(scale.scheduleTimezone)?.subtitle
                    }}</span>
                  </div>

                  <template v-for="(shift, shiftIndex) of scale.shifts" :key="shift.shiftId">
                    <template v-for="(hour, hourIndex) of shift.hours" :key="hour.title">
                      <div
                        class="flex flex-col justify-center items-center font-bold bg-white text-xs shift-hours-column"
                        :style="{ gridRow: `span ${hour.positions.length}` }"
                        :title="shift.title + ' ' + hour.title"
                      >
                        <span
                          class="min-w-0 !whitespace-normal [overflow-wrap:anywhere] [hyphens:auto] text-center hidden md:inline"
                        >
                          <span class="m-0">
                            {{ shift.title }} <br />
                            {{ hour.title }}
                          </span>
                        </span>
                        <span class="md:hidden">
                          {{ shift.title }}<br /><span class="shift-hours-time"
                            >({{ hour.title }})</span
                          >
                        </span>
                      </div>

                      <template
                        v-for="(position, positionIndex) of hour.positions"
                        :key="positionIndex"
                      >
                        <div
                          class="flex flex-col justify-center items-center font-bold bg-white text-xs position-column"
                          :class="boundaryClass(shiftIndex, hourIndex, positionIndex)"
                          :title="position.title"
                        >
                          <span
                            class="m-0 min-w-0 !whitespace-normal [overflow-wrap:anywhere] [hyphens:auto] text-center"
                          >
                            {{ position.title }}</span
                          >
                        </div>
                        <div
                          v-for="(station, stationIndex) of position.stations"
                          :key="stationIndex"
                          class="day-cell"
                          :class="boundaryClass(shiftIndex, hourIndex, positionIndex)"
                        >
                          <div
                            :class="[
                              station.length > 1 && cardIndex !== station.length - 1
                                ? 'station-separator'
                                : '',
                              'card-hover cursor-pointer md:hover:z-10 md:hover:scale-110'
                            ]"
                            v-for="(card, cardIndex) of station"
                            :key="cardIndex"
                          >
                            <unidentified-row
                              v-if="isUnidentifiedCard(card)"
                              :responsible-name="card.coverageResponsibleName ?? null"
                              :note="card.coverageNote ?? null"
                              @click="handleClickStation(company.companyId, card, unity)"
                            />
                            <standard-row
                              v-else-if="!card.opened"
                              @click="handleClickStation(company.companyId, card, unity)"
                              :tableType="card.cell"
                              :photo="card.doctorAvatar ?? undefined"
                              :crm="card.crm"
                              :pendences="card?.type?.pendences"
                              :success="card?.type?.success"
                              :error="card?.type?.error"
                              :waiting="card?.type?.waiting"
                              :hasObservations="!!card.hasObservations"
                              :doctorPending="!!card?.doctorPending"
                              :doctorConfirmed="!!card?.doctorConfirmed"
                              :doctorStationStatus="card?.status"
                              :name="card?.doctorName"
                              :ourTeam="card?.ourTeam"
                              :doctorStationReminderStatus="card?.doctorStationReminderStatus"
                              :isAlert="card?.isAlert"
                              :required-check-not-our-team="
                                card?.requiredCheckNotOurTeam ?? undefined
                              "
                              :required-check-our-team="card?.requiredCheckOurTeam ?? undefined"
                              :exception-summary-status="card?.exceptionSummaryStatus ?? null"
                              :post-station-status="card?.postStationStatus ?? null"
                              :permanence-tooltip="getPermanenceTooltip(card)"
                              :nps-score="card?.doctorNpsScore ?? null"
                            />
                            <vacancy-row
                              v-else
                              @click="handleClickStation(company.companyId, card, unity)"
                            />
                          </div>
                        </div>
                      </template>
                    </template>
                  </template>
                </template>
              </template>
            </template>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import VacancyRow from '@/components/designSystem/Table/StandardRow/vacancy.vue'
import StandardRow from '@/components/designSystem/Table/StandardRow/withIcon.vue'
import UnidentifiedRow from '@/components/designSystem/Table/StandardRow/unidentified.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { computed, defineEmits } from 'vue'
import {
  HealthUnitWeekList,
  ListStationsResponse,
  StationWeekList
} from '@/Network/Types/Responses/StationResponses'
import { timezones } from '@/hooks/useCommon'
import { StationToUpdate } from '../modals/createStation/helpers'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { getGradePermanenceTooltip } from '@/utils/permanence'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { isUnidentifiedCoverage } from '@/utils/coverage'

interface Item {
  label: string
  value: string
}

export default {
  name: 'DashboardView',
  emits: ['openDutyModal', 'showYearDayContent', 'closeModal', 'update-date'],
  props: {
    type: {
      type: String,
      default: 'WEEK'
    },
    weekTableHead: {
      type: Array as () => Item[],
      default: () => []
    },
    weekData: {
      type: Object as () => ListStationsResponse,
      default: () => {}
    }
  },
  components: {
    VacancyRow,
    StandardRow,
    UnidentifiedRow,
    LabelBadge
  },
  setup(props, ctx) {
    const permissionsStore = usePermissionsStore()
    const { flags } = useFeatureFlags()
    const canReadSchedule = (scheduleId: number) => {
      return permissionsStore.check({
        permission: AuthorizationPermissions.ScheduleBasicReading,
        resource: {
          objectType: AuthorizationResourcesAndSubjects.Schedule,
          objectId: scheduleId
        }
      })
    }

    const shouldShowUnity = (unity: HealthUnitWeekList) => {
      const hasUnitPermission = permissionsStore.check({
        permission: AuthorizationPermissions.HealthUnitBasicReading,
        resource: {
          objectType: AuthorizationResourcesAndSubjects.HealthUnit,
          objectId: unity.healthUnitId
        }
      })

      if (hasUnitPermission) return true

      return (unity.schedules || []).some((s) => canReadSchedule(s.scheduleId))
    }

    const createMonthRange = (month: number) => {
      const date = new Date()
      const y = date.getFullYear()
      const firstDay = new Date(y, month, 1)
      const lastDay = new Date(y, month + 1, 0)
      const range = [...Array(lastDay.getDate()).keys()].map((i) => i + firstDay.getDate())
      const startDay = firstDay.getDay() - 1 > 0 ? firstDay.getDay() - 1 : 0
      for (let i = 0; i < startDay; i += 1) {
        range.unshift(-1)
      }

      return { range, startDay }
    }

    const getTimezoneDetails = (time: string) => {
      return timezones.find((t) => t.value === time)
    }

    const abbreviateDay = (label: string): string => {
      const map: Record<string, string> = {
        'Segunda-feira': 'Seg',
        'Terça-feira': 'Ter',
        'Quarta-feira': 'Qua',
        'Quinta-feira': 'Qui',
        'Sexta-feira': 'Sex',
        Sábado: 'Sáb',
        Domingo: 'Dom'
      }
      return map[label] || label.substring(0, 3)
    }

    const boundaryClass = (shiftIndex: number, hourIndex: number, positionIndex: number) => {
      if (shiftIndex === 0 && hourIndex === 0 && positionIndex === 0) return 'boundary-escala'
      if (hourIndex === 0 && positionIndex === 0) return 'boundary-turno'
      if (positionIndex === 0) return 'boundary-horario'
      return ''
    }

    const getPermanenceTooltip = (card: StationWeekList): string | null =>
      // Short-circuit com a flag: o gate visual já existe no `withIcon.vue`,
      // isso só evita o trabalho de varrer os checks a cada card/render com a
      // flag desligada.
      flags.enabledPermanenceRecord
        ? getGradePermanenceTooltip(card.checks ?? [], card.timezone)
        : null

    defineEmits(['openDutyModal', 'showYearDayContent', 'openDutyModal', 'closeModal'])

    const monthRange = computed(() => {
      const date = new Date()
      const month = date.getMonth()
      const { range, startDay } = createMonthRange(month)
      return { range, startDay }
    })

    const handleClickStation = (
      companyId: number,
      card: StationWeekList,
      { healthUnitLatitude, healthUnitLongitude }: HealthUnitWeekList
    ) => {
      const data: StationToUpdate = {
        inCash: card.inCash,
        stationId: card.stationId,
        companyId: companyId,
        doctorId: card.doctorId,
        date: card.date,
        startHour: card.startHour,
        endHour: card.endHour,
        timezone: card.timezone,
        stationType: card.stationType,
        ourTeam: card.ourTeam,
        value: card.value,
        advancePaid: card.advancePaid,
        advanceRequested: card.advanceRequested,
        modifiedValue: card.modifiedValue,
        doctorPending: card.doctorPending,
        urlConviteDoctor: card.urlConviteDoctor,
        observations: [],
        checks: card.checks.map((c) => ({
          ...c,
          checkin: String(c.checkin),
          checkout: c.checkout ? String(c.checkout) : undefined,
          // `isMain` ficou opcional em `HistoryDateRequest` (F2/BF5: o servidor decide,
          // o cliente nunca mais envia no request), mas este é o shape de LEITURA
          // (`StationWeekList.checks`) reaproveitando o mesmo tipo — aqui sempre vem
          // populado pelo backend; o fallback só satisfaz `HistoryDate.isMain: boolean`.
          isMain: c.isMain ?? false
        })),
        position: card.position,
        healthUnitLatitude,
        healthUnitLongitude,
        stationValue: card.stationValue,
        userSponsor: card.doctorStationUserSponsor,
        scheduleId: card.scheduleId
      }
      ctx.emit('openDutyModal', data)
    }

    // Ocupado sem identificação: card hachurado (M1-C) no lugar de médico/vaga aberta
    const isUnidentifiedCard = (card: StationWeekList) => isUnidentifiedCoverage(card)

    return {
      monthRange,
      isUnidentifiedCard,
      handleClickStation,
      getTimezoneDetails,
      canReadSchedule,
      shouldShowUnity,
      abbreviateDay,
      boundaryClass,
      getPermanenceTooltip
    }
  }
}
</script>

<style scoped>
.weekDay {
  background: #fafafa;
  padding: 10px;
  text-align: center;
}

.parent {
  max-height: 100%;
  padding: 0;
  display: grid;
  grid-template-columns: 110px 89px 80px repeat(7, 1fr);
  grid-column-gap: 0;
  grid-row-gap: 0;
  position: relative;
  background: white;
  border-bottom: 1px solid #d8d8e2;
}

.sticky-header {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  position: sticky;
  top: 0;
  z-index: 20;
  background: #fafafa;
}

.sticky-header .weekDay {
  border-bottom: 1px solid #d8d8e2;
  border-right: 1px solid #d8d8e2;
}

.sticky-header .weekDay:nth-child(n + 11) {
  border-bottom: 2px solid #2f7f91;
}

.sticky-column {
  position: sticky;
  left: 0;
  z-index: 10;
  background: white;
  border-top: 1px solid #d8d8e2;
  border-left: 1px solid #d8d8e2;
  border-right: 1px solid #d8d8e2;
}

.shift-hours-column {
  position: sticky;
  left: 111px;
  z-index: 10;
  background: white;
  border-top: 1px solid #d8d8e2;
  border-right: 1px solid #d8d8e2;
}

.position-column {
  position: sticky;
  left: 199px;
  z-index: 9;
  background: white;
  border-right: 1px solid #d8d8e2;
  border-top: 1px solid #d8d8e2;
}

/* --- Hierarquia de divisores horizontais --- */

.day-cell {
  border-right: 1px solid #d8d8e2;
  border-top: 1px solid #d8d8e2;
  background: white;
}

.day-cell:has(:hover) {
  position: relative;
  z-index: 15;
}

.boundary-escala {
  border-top: 3px solid #2f7f91;
}

.boundary-turno {
  border-top: 2px solid #3ca2b9;
}

.boundary-horario {
  border-top: 1px solid #84c8d7;
}

.station-separator {
  position: relative;
}

.station-separator::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: #e8e8ee;
}

@media (min-width: 768px) {
  .card-hover:hover > :deep(*) {
    outline: 2px solid #9ca3af;
    outline-offset: -2px;
    box-shadow:
      0 20px 25px -5px rgb(0 0 0 / 0.1),
      0 8px 10px -6px rgb(0 0 0 / 0.1);
  }
}

.period {
  grid-column: 1 / -1;
  padding: 9px;
  border-top: 1px solid #d8d8e2;
}

.card {
  min-width: 130px;
}

@media screen and (max-width: 767px) {
  .scroll-container {
    position: relative;
  }

  .scroll-container::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 24px;
    background: linear-gradient(to right, transparent, rgba(0, 0, 0, 0.08));
    pointer-events: none;
    z-index: 25;
  }

  .parent {
    grid-template-columns: 24px 16px 20px repeat(7, 1fr);
    grid-column-gap: 0;
    grid-row-gap: 0;
  }

  .card {
    min-width: 80px;
  }

  .boundary-escala {
    border-top-width: 2px;
  }

  .boundary-turno {
    border-top-width: 1px;
  }

  .boundary-horario {
    border-top-width: 1px;
  }

  .sticky-column,
  .shift-hours-column,
  .position-column {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 8px !important;
    font-weight: 600;
    line-height: 1.2;
    padding: 4px 2px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sticky-column {
    left: 0;
  }

  .shift-hours-column {
    left: 25px;
    font-weight: 600;
    padding: 2px 0;
    line-height: 1 !important;
    display: block !important;
    text-align: center;
    overflow: visible !important;
  }

  .shift-hours-time {
    font-size: 6px !important;
    font-weight: 400;
  }

  .position-column {
    left: 42px;
  }

  .weekDay {
    padding: 4px 2px;
    font-size: 10px;
  }

  .sticky-header .weekDay:nth-child(n + 11) {
    top: 28px;
  }

  .parent::-webkit-scrollbar {
    height: 4px;
  }

  .parent::-webkit-scrollbar-track {
    background: #e5e7eb;
    border-radius: 2px;
  }

  .parent::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 2px;
  }
}
</style>
